import React, { useState } from 'react';
import { X, ImageOff } from 'lucide-react';
import type { EcadAutomacaoPasso } from './types';

interface EcadZoomModalProps {
  zoomImage: { stepIndex: number; type: 'real' | 'foco' } | null;
  setZoomImage: (zoom: { stepIndex: number; type: 'real' | 'foco' } | null) => void;
  selectedSubtopicoId: string | null;
  passos?: EcadAutomacaoPasso[];
}

export const EcadZoomModal: React.FC<EcadZoomModalProps> = ({
  zoomImage,
  setZoomImage,
  selectedSubtopicoId,
  passos = []
}) => {
  const [mainImgError, setMainImgError] = useState(false);

  if (zoomImage === null) return null;

  const passo = passos[zoomImage.stepIndex];
  const stepNum = zoomImage.stepIndex + 1;

  // Prioridade: base64 do passo > arquivo estático no disco
  const base64Img = passo?.imagemReal || null;
  const fileRealUrl = "/treinamento/manual_assets/" + selectedSubtopicoId + "/passo_" + stepNum + "_real.png";
  const fileFocoUrl = "/treinamento/manual_assets/" + selectedSubtopicoId + "/passo_" + stepNum + "_foco.png";

  // A imagem principal: se tem base64 usa ele, senão usa file URL
  const getMainSrc = () => {
    if (base64Img) {
      // Base64 serve para ambos os modos (não temos foco em base64, só real)
      return base64Img;
    }
    return zoomImage.type === 'real' ? fileRealUrl : fileFocoUrl;
  };

  const getThumbSrc = (type: 'real' | 'foco') => {
    if (base64Img && type === 'real') return base64Img;
    return type === 'real' ? fileRealUrl : fileFocoUrl;
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(2, 6, 23, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}
      onClick={() => { setZoomImage(null); setMainImgError(false); }}
    >
      <div 
        style={{
          background: '#090d16',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          position: 'relative',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={() => { setZoomImage(null); setMainImgError(false); }}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#ffffff',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <X size={16} />
        </button>

        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#a78bfa' }}>
          Visualizar Imagem - Passo {stepNum}
          {passo?.dryRunStatus && (
            <span style={{ 
              marginLeft: '8px', 
              fontSize: '0.7rem', 
              padding: '2px 8px', 
              borderRadius: '6px', 
              background: passo.dryRunStatus === 'PASS' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              color: passo.dryRunStatus === 'PASS' ? '#34d399' : '#f87171',
              fontWeight: 600
            }}>
              {passo.dryRunStatus === 'PASS' ? '✅ OK' : '❌ FAIL'}
            </span>
          )}
        </h4>

        {/* Large zoomed image */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          overflow: 'hidden', 
          borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.05)',
          background: '#020617',
          minHeight: '200px'
        }}>
          {mainImgError && !base64Img ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#475569', padding: '40px' }}>
              <ImageOff size={32} />
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Sem screenshot disponível</span>
              <span style={{ fontSize: '0.65rem', color: '#334155' }}>Execute o simulador para gerar as capturas</span>
            </div>
          ) : (
            <img 
              src={getMainSrc()} 
              alt={"Passo " + stepNum + " Zoom"} 
              style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block' }}
              onError={() => setMainImgError(true)}
            />
          )}
        </div>

        {/* Dry-run error message */}
        {passo?.dryRunMotivo && (
          <div style={{
            padding: '8px 12px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.15)',
            fontSize: '0.72rem',
            color: '#f87171',
            fontWeight: 500,
            lineHeight: 1.4,
            maxHeight: '60px',
            overflow: 'auto'
          }}>
            ⛔ {passo.dryRunMotivo}
          </div>
        )}

        {/* Switcher gallery below */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '4px' }}>
          <div 
            style={{ 
              cursor: 'pointer', 
              borderRadius: '6px', 
              overflow: 'hidden', 
              border: zoomImage.type === 'real' ? '2px solid #8b5cf6' : '2px solid rgba(255,255,255,0.1)',
              transition: 'all 0.2s'
            }}
            onClick={() => { setZoomImage({ ...zoomImage, type: 'real' }); setMainImgError(false); }}
          >
            <img 
              src={getThumbSrc('real')} 
              alt="Geral"
              style={{ height: '60px', width: '100px', objectFit: 'cover', display: 'block' }}
              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
            />
            <span style={{ display: 'block', textAlign: 'center', fontSize: '0.65rem', color: '#94a3b8', padding: '2px 0' }}>Geral (Full)</span>
          </div>

          <div 
            style={{ 
              cursor: 'pointer', 
              borderRadius: '6px', 
              overflow: 'hidden', 
              border: zoomImage.type === 'foco' ? '2px solid #8b5cf6' : '2px solid rgba(255,255,255,0.1)',
              transition: 'all 0.2s'
            }}
            onClick={() => { setZoomImage({ ...zoomImage, type: 'foco' }); setMainImgError(false); }}
          >
            <img 
              src={getThumbSrc('foco')} 
              alt="Foco"
              style={{ height: '60px', width: '100px', objectFit: 'cover', display: 'block' }}
              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
            />
            <span style={{ display: 'block', textAlign: 'center', fontSize: '0.65rem', color: '#94a3b8', padding: '2px 0' }}>Foco (Recorte)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
